// Pure data transformations for the Tenants page: API shape -> UI helpers.
// No side effects, no API calls — keeps the slice and components thin.

import dayjs from "dayjs";
import { ITenant, ITenantStats, TenantModule, TenantStatus, TenantType } from "../models/interfaces";

// Map backend list payloads (which may be wrapped in { data } or returned raw)
// to a plain ITenant[]. Tolerant of either shape so it survives BE changes.
export const transformTenantsResponse = (response: unknown): ITenant[] => {
	if (Array.isArray(response)) return response as ITenant[];
	if (response && typeof response === "object" && Array.isArray((response as { data?: unknown }).data)) {
		return (response as { data: ITenant[] }).data;
	}
	return [];
};

// Derive the dashboard counters from the loaded tenant list.
export const computeTenantStats = (tenants: ITenant[]): ITenantStats => ({
	active: tenants.filter((tnt) => tnt.status === TenantStatus.Active).length,
	expiring: tenants.filter((tnt) => tnt.status === TenantStatus.Expiring).length,
	decommissioned: tenants.filter((tnt) => tnt.status === TenantStatus.Decommissioned).length,
	total: tenants.length,
});

// Status pill copy + the shared CSS modifier class used to colour it (kcc.css).
export const STATUS_META: Record<TenantStatus, { label: string; cls: string }> = {
	[TenantStatus.Active]: { label: "Active", cls: "kcc-badge--active" },
	[TenantStatus.Expiring]: { label: "Expiring", cls: "kcc-badge--expiring" },
	[TenantStatus.Deactivated]: { label: "Deactivated", cls: "kcc-badge--suspended" },
	[TenantStatus.Decommissioned]: { label: "Decommissioned", cls: "kcc-badge--decommissioned" },
	[TenantStatus.Purged]: { label: "Purged", cls: "kcc-badge--purged" },
};

// Type tag copy + colour class.
export const TYPE_META: Record<TenantType, { label: string; cls: string }> = {
	[TenantType.Kural]: { label: "Kural", cls: "kcc-tag--kural" },
	[TenantType.PXB]: { label: "PXB", cls: "kcc-tag--pxb" },
};

// Two-letter initials for the CST POC avatar (shared KCC helper, re-exported so
// existing consumers keep importing it from the transformer).
export { initials } from "src/utils/kcc";

// ── Bundle display + release-train colour coding ─────────────────────────────
// The reference for "latest" is the CURRENT state of the Dev timeline (release
// cycles), NOT the max observed build: the target train is the newest cycle
// whose Client Deployments phase has started (actual recorded, or today past
// its planned start) — the release that is SUPPOSED to be out. The plan is
// editable, so colours follow it automatically. Distance is measured by
// position in the train sequence (survives numbering jumps like v3.11 → v4.0).

// Deployment-pipeline bundle ids carry a "p-" env prefix — display without it.
export const displayBundle = (value: string | null): string | null =>
	value ? value.replace(/^p-/i, "") : value;

export type BundleTone = "green" | "amber" | "red" | "none";

// Utilization colour bands (FR-14): red at the tenant's OWN alert threshold
// (falls back to the server default — KCC_UTILIZATION_ALERT_PCT, 70) so the
// dot can never stay green/amber after the backend has already emailed an
// over-budget alert; amber >= 50.
export const utilizationTone = (pct: number | null | undefined, alertPct?: number | null): BundleTone => {
	if (pct == null) return "none";
	const red = alertPct != null && alertPct > 0 ? alertPct : 70;
	if (pct >= red) return "red";
	if (pct >= 50) return "amber";
	return "green";
};

export interface ICycleForBundle {
	version: string;
	cycle_start: string;
	phases: { key: string; planned_start: string; actual_start: string | null }[];
}

const MAJOR_MINOR_PATCH_RE = /^v?(\d+)\.(\d+)\.(\d+)$/;

// major.minor key for a train version ("v3.7" → "3.7").
const trainKey = (version: string): string => version.trim().toLowerCase().replace(/^v/, "");

export const bundleStatus = (
	bundle: string | null,
	cycles?: ICycleForBundle[] | null,
	today?: string,
): { display: string | null; tone: BundleTone; hint: string } => {
	const display = displayBundle(bundle);
	if (!display) return { display, tone: "none", hint: "" };

	const match = MAJOR_MINOR_PATCH_RE.exec(display);
	if (!match) {
		// Not major.minor.patch → definitely red (legacy ids like v514, v263).
		return { display, tone: "red", hint: "Non-standard bundle version" };
	}
	const tenantTrain = `${Number(match[1])}.${Number(match[2])}`;

	// Without timeline data there is no reference — no colour, never a false one.
	if (!cycles || cycles.length === 0) return { display, tone: "none", hint: "" };

	// Train sequence in rollout order.
	const ordered = [...cycles].sort((a, b) => a.cycle_start.localeCompare(b.cycle_start));
	const keys = ordered.map((c) => trainKey(c.version));

	// Target = newest train whose Client Deployments phase has begun. A
	// recorded actual start governs when present — a FUTURE actual means
	// "scheduled, not started yet" and must not advance the target (seen live
	// 2026-09-09: v3.8's deploy pre-filled with a future date turned every
	// v3.7 tenant amber a week early). With no actual, the plan date decides.
	const now = today ?? new Date().toISOString().slice(0, 10);
	let targetIdx = -1;
	ordered.forEach((c, i) => {
		const deploy = c.phases.find((p) => p.key === "client_deploy");
		if (!deploy) return;
		const begun = deploy.actual_start ? deploy.actual_start <= now : now >= deploy.planned_start;
		if (begun) targetIdx = i;
	});
	if (targetIdx === -1) return { display, tone: "none", hint: "" };
	const target = keys[targetIdx];

	const tenantIdx = keys.indexOf(tenantTrain);
	if (tenantIdx === -1) {
		// Not a known train: numerically ahead of the target (early adopter /
		// hotfix) reads green; older than anything planned reads red.
		const [tMaj, tMin] = tenantTrain.split(".").map(Number);
		const [gMaj, gMin] = target.split(".").map(Number);
		const ahead = tMaj > gMaj || (tMaj === gMaj && tMin > gMin);
		return ahead
			? { display, tone: "green", hint: "Ahead of the current release" }
			: { display, tone: "red", hint: "Unknown train, older than the current release" };
	}

	const behind = targetIdx - tenantIdx;
	if (behind <= 0) return { display, tone: "green", hint: `On the current release (v${target})` };
	if (behind === 1) return { display, tone: "amber", hint: `1 release behind (v${target} is rolling out)` };
	return { display, tone: "red", hint: `${behind} releases behind (current: v${target})` };
};

// Human-readable expiry date (e.g. "24 July 2026"); "---" when there isn't one.
export const formatExpiry = (iso: string | null): string =>
	iso ? dayjs(iso).format("DD MMMM YYYY") : "---";

// Display label for the instance environment. Anything unknown/missing (e.g.
// a row from a backend older than the column) renders as Prod — matching the
// DB backfill, which assumes all pre-existing tenants are Prod.
export const envLabel = (env?: string | null): string => (env === "uat" ? "UAT" : "Prod");

// Kural modules a tenant can enable — display order + labels for the onboard
// chips and the Overview row. Keys mirror the backend's ModuleKey exactly.
export const MODULE_OPTIONS: { key: TenantModule; label: string }[] = [
	{ key: "qual_market_research", label: "Qualitative Market Research" },
	{ key: "reports", label: "Reports" },
	{ key: "quant_market_research", label: "Quantitative Market Research" },
	{ key: "research_notes", label: "Research Notes" },
	{ key: "web_boards", label: "Web Boards" },
];

// Label for a stored module key; unknown keys render as-is (defensive against
// a backend newer than this build).
export const moduleLabel = (key: string): string =>
	MODULE_OPTIONS.find((option) => option.key === key)?.label ?? key;

// (The tenants CSV export was removed 2026-08-19 — product decision: the
// Export button wasn't needed. csvCell stays in utils for the Audit export.)

// Filter the list by the active status pill.
export const filterTenants = (
	tenants: ITenant[],
	statusFilter: TenantStatus | "all",
	search: string,
): ITenant[] => {
	const term = search.trim().toLowerCase();
	return tenants.filter((tnt) => {
		const matchesStatus = statusFilter === "all" || tnt.status === statusFilter;
		// project_code is nullable (a blank Overview edit persists null) — the
		// unguarded dereference white-screened the whole app on search (P0-1).
		const matchesSearch =
			!term ||
			tnt.tenant_name.toLowerCase().includes(term) ||
			tnt.tenant_code.toLowerCase().includes(term) ||
			(tnt.project_code ?? "").toLowerCase().includes(term);
		return matchesStatus && matchesSearch;
	});
};
